import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Lock, SignOut, EnvelopeSimple, UsersThree, CalendarCheck, CurrencyDollar, Spinner, Ticket, Plus, Article, Trash, GearSix, Eye, X, UploadSimple, Hourglass,
} from "@phosphor-icons/react";
import { API } from "@/lib/site";

const TABS = [
  { id: "messages", label: "Messages", icon: EnvelopeSimple },
  { id: "bookings", label: "Bookings", icon: CalendarCheck },
  { id: "waitlist", label: "Waitlist", icon: Hourglass },
  { id: "subscribers", label: "Subscribers", icon: UsersThree },
  { id: "sales", label: "Sales", icon: CurrencyDollar },
  { id: "coupons", label: "Coupons", icon: Ticket },
  { id: "blog", label: "Blog", icon: Article },
  { id: "settings", label: "Settings", icon: GearSix },
];

const REMINDER_CHOICES = [
  { h: 48, label: "48 hours before" },
  { h: 24, label: "24 hours before" },
  { h: 2, label: "2 hours before" },
  { h: 1, label: "1 hour before" },
];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("tb_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [loggingIn, setLoggingIn] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("messages");
  const [coupons, setCoupons] = useState([]);
  const [couponForm, setCouponForm] = useState({ code: "", discount_type: "percent", value: "", scope: "all" });
  const [creatingCoupon, setCreatingCoupon] = useState(false);

  const loadCoupons = useCallback(async (tk) => {
    try {
      const res = await fetch(`${API}/admin/coupons`, { headers: { Authorization: `Bearer ${tk}` } });
      if (res.ok) {
        const d = await res.json();
        setCoupons(d.coupons || []);
      }
    } catch { /* ignore */ }
  }, []);

  const createCoupon = async (e) => {
    e.preventDefault();
    if (!couponForm.code || !couponForm.value) return toast.error("Enter a code and a value.");
    setCreatingCoupon(true);
    try {
      const res = await fetch(`${API}/admin/coupons`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...couponForm, value: parseFloat(couponForm.value) }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Could not create code");
      toast.success(`Code ${d.code} created`);
      setCouponForm({ code: "", discount_type: "percent", value: "", scope: "all" });
      loadCoupons(token);
    } catch (err) {
      toast.error(err.message || "Could not create code");
    } finally {
      setCreatingCoupon(false);
    }
  };

  const deactivateCoupon = async (id) => {
    try {
      await fetch(`${API}/admin/coupons/${id}/deactivate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Code deactivated");
      loadCoupons(token);
    } catch {
      toast.error("Could not deactivate");
    }
  };

  const [blogPosts, setBlogPosts] = useState([]);
  const [blogForm, setBlogForm] = useState({ title: "", tag: "Journal", excerpt: "", body: "", image: "", published: true, schedule: "", ebook_lookup_key: "" });
  const [savingPost, setSavingPost] = useState(false);
  const [ebookOptions, setEbookOptions] = useState([]);

  useEffect(() => {
    fetch(`${API}/ebooks`)
      .then((r) => r.json())
      .then((d) => setEbookOptions([...(d.ebooks || []), ...(d.bundle ? [d.bundle] : [])]))
      .catch(() => {});
  }, []);

  const loadBlog = useCallback(async (tk) => {
    try {
      const res = await fetch(`${API}/admin/blog`, { headers: { Authorization: `Bearer ${tk}` } });
      if (res.ok) {
        const d = await res.json();
        setBlogPosts(d.posts || []);
      }
    } catch { /* ignore */ }
  }, []);

  const createPost = async (e) => {
    e.preventDefault();
    if (!blogForm.title.trim()) return toast.error("Add a title.");
    setSavingPost(true);
    try {
      const { schedule, ...rest } = blogForm;
      const payload = {
        ...rest,
        publish_at: !blogForm.published && schedule ? new Date(schedule).toISOString() : null,
        site_url: window.location.origin,
      };
      const res = await fetch(`${API}/admin/blog${editingId ? `/${editingId}` : ""}`, {
        method: editingId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      toast.success(editingId ? "Post updated" : "Post saved");
      setEditingId(null);
      setBlogForm({ title: "", tag: "Journal", excerpt: "", body: "", image: "", published: true, schedule: "", ebook_lookup_key: "" });
      loadBlog(token);
    } catch {
      toast.error("Could not save post");
    } finally {
      setSavingPost(false);
    }
  };

  const deletePost = async (id) => {
    try {
      await fetch(`${API}/admin/blog/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      toast.success("Post deleted");
      loadBlog(token);
    } catch {
      toast.error("Could not delete");
    }
  };

  const [uploading, setUploading] = useState(false);
  const uploadImage = async (fileObj) => {
    if (!fileObj) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const res = await fetch(`${API}/admin/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Upload failed");
      setBlogForm((f) => ({ ...f, image: `${API}/files/${d.file_id}` }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const togglePublish = async (p) => {
    try {
      await fetch(`${API}/admin/blog/${p.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ published: !p.published }),
      });
      loadBlog(token);
    } catch {
      toast.error("Could not update");
    }
  };

  const [previewPost, setPreviewPost] = useState(null);
  const openPreview = async (id) => {
    try {
      const res = await fetch(`${API}/admin/blog/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      setPreviewPost(d.post);
    } catch {
      toast.error("Could not load preview");
    }
  };

  const [reminderOffsets, setReminderOffsets] = useState([24, 1]);
  const [nudgeHours, setNudgeHours] = useState(3);
  const [editingId, setEditingId] = useState(null);
  const [spotlight, setSpotlight] = useState({ enabled: true, items: [] });
  const [heroUrl, setHeroUrl] = useState("");
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const loadSettings = useCallback(async (tk) => {
    try {
      const [r, s, h] = await Promise.all([
        fetch(`${API}/admin/settings/reminders`, { headers: { Authorization: `Bearer ${tk}` } }),
        fetch(`${API}/admin/settings/spotlight`, { headers: { Authorization: `Bearer ${tk}` } }),
        fetch(`${API}/admin/settings/hero`, { headers: { Authorization: `Bearer ${tk}` } }),
      ]);
      if (r.ok) {
        const rd = await r.json();
        setReminderOffsets(rd.offsets || [24, 1]);
        setNudgeHours(rd.nudge_hours ?? 3);
      }
      if (s.ok) setSpotlight(await s.json());
      if (h.ok) setHeroUrl((await h.json()).url || "");
    } catch { /* ignore */ }
  }, []);

  const editPost = async (id) => {
    try {
      const res = await fetch(`${API}/admin/blog/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      const p = d.post;
      setBlogForm({
        title: p.title, tag: p.tag, excerpt: p.excerpt || "", body: p.body || "",
        image: p.image || "", published: p.published,
        schedule: p.publish_at ? new Date(p.publish_at).toISOString().slice(0, 16) : "",
        ebook_lookup_key: p.ebook_lookup_key || "",
      });
      setEditingId(id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Could not load post");
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setBlogForm({ title: "", tag: "Journal", excerpt: "", body: "", image: "", published: true, schedule: "", ebook_lookup_key: "" });
  };

  const toggleOffset = (h) =>
    setReminderOffsets((o) => (o.includes(h) ? o.filter((x) => x !== h) : [...o, h]));

  const saveReminders = async () => {
    setSavingSettings(true);
    try {
      await fetch(`${API}/admin/settings/reminders`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ offsets: reminderOffsets, nudge_hours: Number(nudgeHours) }),
      });
      toast.success("Reminder settings saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSavingSettings(false);
    }
  };

  const saveSpotlight = async () => {
    setSavingSettings(true);
    try {
      await fetch(`${API}/admin/settings/spotlight`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(spotlight),
      });
      toast.success("Featured quote saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSavingSettings(false);
    }
  };

  const saveHero = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch(`${API}/admin/settings/hero`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url: heroUrl.trim() }),
      });
      if (!res.ok) throw new Error();
      toast.success("Homepage video saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSavingSettings(false);
    }
  };

  const uploadHeroVideo = async (fileObj) => {
    if (!fileObj) return;
    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const res = await fetch(`${API}/admin/upload/video`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Upload failed");
      setHeroUrl(`${API}/files/${d.file_id}`);
      toast.success("Video uploaded — click Save to publish");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploadingVideo(false);
    }
  };

  const deleteWaitlistEntry = async (id) => {
    if (!window.confirm("Remove this waitlist entry?")) return;
    try {
      const res = await fetch(`${API}/admin/waitlist/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setData((prev) => prev ? {
        ...prev,
        waitlist: (prev.waitlist || []).filter((w) => w.id !== id),
        counts: { ...prev.counts, waitlist: Math.max(0, (prev.counts?.waitlist || 1) - 1) },
      } : prev);
      toast.success("Waitlist entry removed");
    } catch {
      toast.error("Could not remove");
    }
  };

  const loadOverview = useCallback(async (tk) => {
    try {
      const res = await fetch(`${API}/admin/overview`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      if (res.status === 401) {
        setAuthed(false);
        localStorage.removeItem("tb_admin_token");
        return;
      }
      const d = await res.json();
      setData(d);
      setAuthed(true);
      loadCoupons(tk);
      loadBlog(tk);
      loadSettings(tk);
    } catch {
      toast.error("Could not load data.");
    } finally {
      setChecking(false);
    }
  }, [loadCoupons, loadBlog, loadSettings]);

  useEffect(() => {
    if (token) loadOverview(token);
    else setChecking(false);
  }, [token, loadOverview]);

  const handleTab = (id) => {
    setTab(id);
    if ((id === "messages" || id === "bookings") && (data?.counts?.[`new_${id}`] || 0) > 0) {
      fetch(`${API}/admin/seen/${id}`, { method: "POST", headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      setData((prev) => prev ? { ...prev, counts: { ...prev.counts, [`new_${id}`]: 0 } } : prev);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(creds),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Login failed");
      localStorage.setItem("tb_admin_token", d.token);
      setToken(d.token);
      toast.success("Welcome back, Tony.");
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("tb_admin_token");
    setToken("");
    setAuthed(false);
    setData(null);
  };

  if (checking) {
    return (
      <main className="min-h-screen bg-brand-bg flex items-center justify-center">
        <Spinner size={40} className="animate-spin text-brand-gold" />
      </main>
    );
  }

  if (!authed) {
    return (
      <main className="min-h-screen bg-brand-bg flex items-center justify-center px-6">
        <motion.form
          onSubmit={login}
          data-testid="admin-login-form"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md rounded-3xl border border-brand-line bg-brand-surface p-10"
        >
          <div className="flex items-center gap-3 mb-8">
            <Lock size={22} className="text-brand-gold" />
            <span className="font-serif text-2xl text-brand-text">TonyBrand29 Admin</span>
          </div>
          <label className="overline block mb-2">Email</label>
          <input
            data-testid="admin-email"
            type="email"
            value={creds.email}
            onChange={(e) => setCreds({ ...creds, email: e.target.value })}
            placeholder="you@email.com"
            className="w-full bg-transparent border-b border-brand-line py-3 mb-6 text-brand-text focus:outline-none focus:border-brand-gold transition-colors"
          />
          <label className="overline block mb-2">Password</label>
          <input
            data-testid="admin-password"
            type="password"
            value={creds.password}
            onChange={(e) => setCreds({ ...creds, password: e.target.value })}
            placeholder="••••••••"
            className="w-full bg-transparent border-b border-brand-line py-3 mb-8 text-brand-text focus:outline-none focus:border-brand-gold transition-colors"
          />
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loggingIn}
            className="w-full rounded-full bg-brand-text py-4 text-sm font-medium text-brand-bg hover:bg-brand-gold transition-colors disabled:opacity-60"
          >
            {loggingIn ? "Signing in…" : "Sign in"}
          </button>
        </motion.form>
      </main>
    );
  }

  const rows = data?.[tab] || [];

  return (
    <main className="min-h-screen bg-brand-bg text-brand-text">
      <header className="glass border-b border-brand-line sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <span className="font-serif text-2xl">
            TonyBrand<span className="text-brand-gold">29</span>
            <span className="text-brand-dim text-base ml-3">Admin</span>
          </span>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-full border border-brand-line px-5 py-2.5 text-sm text-brand-dim hover:text-brand-text hover:border-brand-gold transition-colors"
          >
            <SignOut size={16} /> Log out
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => handleTab(t.id)}
              className={`relative rounded-2xl border p-5 text-left transition-colors ${
                tab === t.id ? "border-brand-gold bg-brand-surface" : "border-brand-line hover:border-brand-sage/50"
              }`}
            >
              {(t.id === "messages" || t.id === "bookings") && (data?.counts?.[`new_${t.id}`] || 0) > 0 && (
                <span
                  data-testid={`admin-badge-${t.id}`}
                  className="absolute top-3 right-3 min-w-[22px] h-[22px] px-1.5 grid place-items-center rounded-full bg-brand-gold text-brand-bg text-xs font-bold animate-pulse"
                >
                  {data.counts[`new_${t.id}`]}
                </span>
              )}
              <t.icon size={22} className="text-brand-gold mb-3" />
              <div className="font-serif text-3xl">
                {t.id === "coupons" ? coupons.length : t.id === "blog" ? blogPosts.length : t.id === "settings" ? "·" : data?.counts?.[t.id] ?? 0}
              </div>
              <div className="overline mt-1">{t.label}</div>
            </button>
          ))}
        </div>

        {tab === "coupons" ? (
          <div className="space-y-6" data-testid="admin-coupons">
            <form onSubmit={createCoupon} data-testid="coupon-form" className="rounded-3xl border border-brand-line bg-brand-surface p-6 grid sm:grid-cols-5 gap-4 items-end">
              <div>
                <label className="overline block mb-2">Code</label>
                <input data-testid="coupon-code" value={couponForm.code} onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })} placeholder="LAUNCH20" className="w-full bg-transparent border-b border-brand-line py-2 text-brand-text focus:outline-none focus:border-brand-gold" />
              </div>
              <div>
                <label className="overline block mb-2">Type</label>
                <select data-testid="coupon-type" value={couponForm.discount_type} onChange={(e) => setCouponForm({ ...couponForm, discount_type: e.target.value })} className="w-full bg-brand-surface2 border border-brand-line rounded-lg py-2 px-2 text-brand-text focus:outline-none">
                  <option value="percent">% off</option>
                  <option value="amount">$ off</option>
                </select>
              </div>
              <div>
                <label className="overline block mb-2">Value</label>
                <input data-testid="coupon-value" type="number" value={couponForm.value} onChange={(e) => setCouponForm({ ...couponForm, value: e.target.value })} placeholder={couponForm.discount_type === "percent" ? "20" : "10"} className="w-full bg-transparent border-b border-brand-line py-2 text-brand-text focus:outline-none focus:border-brand-gold" />
              </div>
              <div>
                <label className="overline block mb-2">Applies to</label>
                <select data-testid="coupon-scope" value={couponForm.scope} onChange={(e) => setCouponForm({ ...couponForm, scope: e.target.value })} className="w-full bg-brand-surface2 border border-brand-line rounded-lg py-2 px-2 text-brand-text focus:outline-none">
                  <option value="all">Everything</option>
                  <option value="ebooks">Ebooks</option>
                  <option value="sessions">Sessions</option>
                </select>
              </div>
              <button data-testid="coupon-create" type="submit" disabled={creatingCoupon} className="rounded-full bg-brand-gold px-5 py-3 text-sm font-medium text-brand-bg hover:bg-brand-text transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
                <Plus size={16} /> Create
              </button>
            </form>

            <div className="rounded-3xl border border-brand-line bg-brand-surface overflow-hidden">
              {coupons.length === 0 ? (
                <p className="text-brand-dim p-10 text-center">No codes yet. Create your first above.</p>
              ) : (
                <div className="divide-y divide-brand-line">
                  {coupons.map((c) => (
                    <div key={c.id} data-testid={`coupon-row-${c.code}`} className="p-6 flex flex-wrap items-center gap-4 md:gap-6">
                      <span className="font-mono text-brand-text text-lg">{c.code}</span>
                      <span className="text-brand-gold">{c.percent_off ? `${c.percent_off}% off` : `$${c.amount_off} off`}</span>
                      <span className="overline">{c.scope}</span>
                      <span className="text-xs text-brand-dim">used {c.times_redeemed}×</span>
                      <span className="flex-1" />
                      {c.active ? (
                        <button data-testid={`coupon-deactivate-${c.code}`} onClick={() => deactivateCoupon(c.id)} className="text-sm text-brand-dim hover:text-brand-gold transition-colors">
                          Deactivate
                        </button>
                      ) : (
                        <span className="text-xs text-brand-dim/60">inactive</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : tab === "blog" ? (
          <div className="space-y-6" data-testid="admin-blog">
            <form onSubmit={createPost} data-testid="blog-form" className="rounded-3xl border border-brand-line bg-brand-surface p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="blog-title" value={blogForm.title} onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })} placeholder="Post title" className="w-full bg-transparent border-b border-brand-line py-2 text-brand-text focus:outline-none focus:border-brand-gold" />
                <input data-testid="blog-tag" value={blogForm.tag} onChange={(e) => setBlogForm({ ...blogForm, tag: e.target.value })} placeholder="Tag (e.g. Wealth)" className="w-full bg-transparent border-b border-brand-line py-2 text-brand-text focus:outline-none focus:border-brand-gold" />
              </div>
              <input data-testid="blog-image" value={blogForm.image} onChange={(e) => setBlogForm({ ...blogForm, image: e.target.value })} placeholder="Cover image URL (or upload below)" className="w-full bg-transparent border-b border-brand-line py-2 text-brand-text focus:outline-none focus:border-brand-gold" />
              <div className="flex items-center gap-4">
                <label className="inline-flex items-center gap-2 rounded-full border border-brand-line px-4 py-2 text-sm text-brand-text hover:border-brand-gold cursor-pointer transition-colors">
                  <UploadSimple size={16} /> {uploading ? "Uploading…" : "Upload cover"}
                  <input data-testid="blog-image-upload" type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
                </label>
                {blogForm.image && (
                  <div className="flex items-center gap-2">
                    <img src={blogForm.image} alt="cover preview" data-testid="blog-image-preview" className="h-12 w-20 object-cover rounded-lg border border-brand-line" />
                    <button type="button" data-testid="blog-image-remove" onClick={() => setBlogForm({ ...blogForm, image: "" })} className="text-xs text-brand-dim hover:text-brand-gold transition-colors">Remove</button>
                  </div>
                )}
              </div>
              <input data-testid="blog-excerpt" value={blogForm.excerpt} onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })} placeholder="Short summary shown on the card" className="w-full bg-transparent border-b border-brand-line py-2 text-brand-text focus:outline-none focus:border-brand-gold" />
              <textarea data-testid="blog-body" rows={8} value={blogForm.body} onChange={(e) => setBlogForm({ ...blogForm, body: e.target.value })} placeholder="Write your post here…" className="w-full bg-transparent border border-brand-line rounded-xl p-3 text-brand-text focus:outline-none focus:border-brand-gold resize-none" />
              <div>
                <label className="text-sm text-brand-dim block mb-1">Link a book — adds a Read the full book button on the post</label>
                <select
                  data-testid="blog-ebook-link"
                  value={blogForm.ebook_lookup_key}
                  onChange={(e) => setBlogForm({ ...blogForm, ebook_lookup_key: e.target.value })}
                  className="w-full bg-brand-surface2 border border-brand-line rounded-lg px-3 py-2 text-brand-text focus:outline-none focus:border-brand-gold"
                >
                  <option value="">No book linked</option>
                  {ebookOptions.map((b) => (
                    <option key={b.lookup_key} value={b.lookup_key}>{b.title} — ${b.price.toFixed(0)}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-brand-dim">
                    <input type="checkbox" data-testid="blog-published" checked={blogForm.published} onChange={(e) => setBlogForm({ ...blogForm, published: e.target.checked })} /> Publish now
                  </label>
                  {!blogForm.published && (
                    <label className="flex items-center gap-2 text-sm text-brand-dim">
                      Schedule:
                      <input type="datetime-local" data-testid="blog-schedule" value={blogForm.schedule} onChange={(e) => setBlogForm({ ...blogForm, schedule: e.target.value })} className="bg-brand-surface2 border border-brand-line rounded-lg px-2 py-1 text-brand-text focus:outline-none" />
                    </label>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {editingId && (
                    <button type="button" data-testid="blog-cancel-edit" onClick={cancelEdit} className="text-sm text-brand-dim hover:text-brand-text transition-colors">
                      Cancel edit
                    </button>
                  )}
                  <button data-testid="blog-create" type="submit" disabled={savingPost} className="rounded-full bg-brand-gold px-6 py-3 text-sm font-medium text-brand-bg hover:bg-brand-text transition-colors inline-flex items-center gap-2 disabled:opacity-60">
                    <Plus size={16} /> {savingPost ? "Saving…" : editingId ? "Save changes" : blogForm.published ? "Publish post" : blogForm.schedule ? "Schedule post" : "Save draft"}
                  </button>
                </div>
              </div>
            </form>

            <div className="rounded-3xl border border-brand-line bg-brand-surface overflow-hidden">
              {blogPosts.length === 0 ? (
                <p className="text-brand-dim p-10 text-center">No posts yet. Write your first above.</p>
              ) : (
                <div className="divide-y divide-brand-line">
                  {blogPosts.map((p) => (
                    <div key={p.id} data-testid={`blog-row-${p.slug}`} className="p-6 flex items-center gap-4">
                      <div className="flex-1">
                        <p className="text-brand-text">{p.title}</p>
                        <p className="text-xs text-brand-dim">{p.tag} · {p.read} · {p.status === "scheduled" ? `Scheduled ${p.publish_at ? new Date(p.publish_at).toLocaleString() : ""}` : p.published ? "Published" : "Draft"}</p>
                      </div>
                      <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-sm text-brand-gold hover:text-brand-text transition-colors">View</a>
                      <button data-testid={`blog-edit-${p.slug}`} onClick={() => editPost(p.id)} className="text-xs text-brand-dim hover:text-brand-gold transition-colors">Edit</button>
                      <button data-testid={`blog-preview-${p.slug}`} onClick={() => openPreview(p.id)} className="text-brand-dim hover:text-brand-gold transition-colors" title="Preview">
                        <Eye size={16} />
                      </button>
                      <button data-testid={`blog-toggle-${p.slug}`} onClick={() => togglePublish(p)} className="text-xs text-brand-dim hover:text-brand-gold transition-colors">
                        {p.published ? "Unpublish" : "Publish"}
                      </button>
                      <button data-testid={`blog-delete-${p.slug}`} onClick={() => deletePost(p.id)} className="text-brand-dim hover:text-brand-gold transition-colors">
                        <Trash size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : tab === "settings" ? (
          <div className="space-y-6" data-testid="admin-settings">
            <div className="rounded-3xl border border-brand-line bg-brand-surface p-6">
              <p className="font-serif text-2xl text-brand-text mb-1">Reminder timing</p>
              <p className="text-sm text-brand-dim mb-5">When should clients get an email reminder before their session?</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {REMINDER_CHOICES.map((c) => (
                  <label key={c.h} data-testid={`reminder-choice-${c.h}`} className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${reminderOffsets.includes(c.h) ? "border-brand-gold bg-brand-gold/10" : "border-brand-line"}`}>
                    <input type="checkbox" checked={reminderOffsets.includes(c.h)} onChange={() => toggleOffset(c.h)} />
                    <span className="text-brand-text">{c.label}</span>
                  </label>
                ))}
              </div>
              <button data-testid="save-reminders" onClick={saveReminders} disabled={savingSettings} className="mt-5 rounded-full bg-brand-gold px-6 py-3 text-sm font-medium text-brand-bg hover:bg-brand-text transition-colors disabled:opacity-60">
                Save reminder timing
              </button>
              <div className="mt-6 pt-6 border-t border-brand-line">
                <p className="text-brand-text mb-1">No-show nudge</p>
                <p className="text-sm text-brand-dim mb-3">Email clients who haven't tapped "I'll be there" this many hours before their session (0 to turn off).</p>
                <div className="flex items-center gap-3">
                  <input type="number" min="0" max="72" data-testid="nudge-hours" value={nudgeHours} onChange={(e) => setNudgeHours(e.target.value)} className="w-24 bg-brand-surface2 border border-brand-line rounded-lg px-3 py-2 text-brand-text focus:outline-none focus:border-brand-gold" />
                  <span className="text-sm text-brand-dim">hours before</span>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-brand-line bg-brand-surface p-6">
              <p className="font-serif text-2xl text-brand-text mb-1">Featured testimonial</p>
              <p className="text-sm text-brand-dim mb-5">Spotlight one client quote near the top of your homepage.</p>
              <label className="flex items-center gap-2 text-sm text-brand-dim mb-4">
                <input type="checkbox" data-testid="spotlight-enabled" checked={spotlight.enabled} onChange={(e) => setSpotlight({ ...spotlight, enabled: e.target.checked })} /> Show on homepage
              </label>
              <div className="space-y-4">
                {(spotlight.items || []).map((it, i) => (
                  <div key={i} data-testid={`spotlight-item-${i}`} className="rounded-2xl border border-brand-line p-4 space-y-3">
                    <textarea data-testid={`spotlight-quote-${i}`} rows={2} value={it.quote} onChange={(e) => setSpotlight({ ...spotlight, items: spotlight.items.map((x, j) => j === i ? { ...x, quote: e.target.value } : x) })} placeholder="Client quote…" className="w-full bg-transparent border-b border-brand-line py-2 text-brand-text focus:outline-none focus:border-brand-gold resize-none" />
                    <div className="flex items-center gap-3">
                      <input data-testid={`spotlight-attribution-${i}`} value={it.attribution} onChange={(e) => setSpotlight({ ...spotlight, items: spotlight.items.map((x, j) => j === i ? { ...x, attribution: e.target.value } : x) })} placeholder="Client A" className="flex-1 bg-transparent border-b border-brand-line py-2 text-brand-text focus:outline-none focus:border-brand-gold" />
                      <button type="button" data-testid={`spotlight-remove-${i}`} onClick={() => setSpotlight({ ...spotlight, items: spotlight.items.filter((_, j) => j !== i) })} className="text-brand-dim hover:text-brand-gold">
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" data-testid="spotlight-add" onClick={() => setSpotlight({ ...spotlight, items: [...(spotlight.items || []), { quote: "", attribution: "Client" }] })} className="mt-4 inline-flex items-center gap-2 rounded-full border border-brand-line px-4 py-2 text-sm text-brand-text hover:border-brand-gold transition-colors">
                <Plus size={14} /> Add quote
              </button>
              <div>
                <button data-testid="save-spotlight" onClick={saveSpotlight} disabled={savingSettings} className="mt-5 rounded-full bg-brand-gold px-6 py-3 text-sm font-medium text-brand-bg hover:bg-brand-text transition-colors disabled:opacity-60">
                  Save featured quotes
                </button>
              </div>
            </div>

            <div className="rounded-3xl border border-brand-line bg-brand-surface p-6" data-testid="hero-video-settings">
              <p className="font-serif text-2xl text-brand-text mb-1">Homepage welcome video</p>
              <p className="text-sm text-brand-dim mb-5">Paste a YouTube or Vimeo link, or upload a video file. A play button appears on your hero portrait. Leave blank to hide it.</p>
              <input
                data-testid="hero-video-url"
                value={heroUrl}
                onChange={(e) => setHeroUrl(e.target.value)}
                placeholder="https://youtube.com/watch?v=…  or  https://vimeo.com/…"
                className="w-full bg-transparent border-b border-brand-line py-2 text-brand-text focus:outline-none focus:border-brand-gold"
              />
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <label className="inline-flex items-center gap-2 rounded-full border border-brand-line px-4 py-2 text-sm text-brand-text hover:border-brand-gold transition-colors cursor-pointer">
                  <UploadSimple size={16} /> {uploadingVideo ? "Uploading…" : "Upload video file"}
                  <input data-testid="hero-video-upload" type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => uploadHeroVideo(e.target.files?.[0])} />
                </label>
                <span className="text-xs text-brand-dim/60">MP4, WEBM or MOV · under 50MB</span>
              </div>
              {heroUrl && (
                <button data-testid="hero-video-clear" onClick={() => setHeroUrl("")} className="mt-4 ml-1 inline-flex items-center gap-2 text-sm text-brand-dim hover:text-red-400 transition-colors">
                  <Trash size={14} /> Clear video
                </button>
              )}
              <div>
                <button data-testid="save-hero" onClick={saveHero} disabled={savingSettings} className="mt-5 rounded-full bg-brand-gold px-6 py-3 text-sm font-medium text-brand-bg hover:bg-brand-text transition-colors disabled:opacity-60">
                  Save homepage video
                </button>
              </div>
            </div>
          </div>
        ) : (
        <div className="rounded-3xl border border-brand-line bg-brand-surface overflow-hidden" data-testid="admin-table">
          {rows.length === 0 ? (
            <p className="text-brand-dim p-10 text-center">Nothing here yet.</p>
          ) : (
            <div className="divide-y divide-brand-line">
              {rows.map((r, i) => (
                <div key={i} className="p-6 flex flex-col md:flex-row md:items-center gap-2 md:gap-6">
                  {tab === "messages" && (
                    <>
                      <div className="md:w-48">
                        <p className="text-brand-text">{r.name}</p>
                        <p className="text-sm text-brand-gold">{r.email}</p>
                      </div>
                      <p className="text-brand-dim flex-1">{r.message}</p>
                      <span className="text-xs text-brand-dim/60">{(r.created_at || "").slice(0, 10)}</span>
                    </>
                  )}
                  {tab === "bookings" && (
                    <>
                      <div className="md:w-48">
                        <p className="text-brand-text">{r.name}</p>
                        <p className="text-sm text-brand-gold">{r.email}</p>
                      </div>
                      <div className="flex-1">
                        <p className="text-brand-text">{r.session_name}</p>
                        <p className="text-sm text-brand-dim">
                          {r.date} · {r.start_time} {r.tz_label} · {r.status}
                        </p>
                        {r.meeting_link && (
                          <a href={r.meeting_link} target="_blank" rel="noreferrer" className="text-xs text-brand-gold break-all">
                            {r.meeting_link}
                          </a>
                        )}
                      </div>
                      <span className="text-sm text-brand-text">
                        {r.price > 0 ? `$${r.price.toFixed(0)}` : "Free"}
                      </span>
                    </>
                  )}
                  {tab === "subscribers" && (
                    <>
                      <p className="text-brand-text flex-1">{r.email}</p>
                      <span className="text-xs text-brand-dim/60">{(r.created_at || "").slice(0, 10)}</span>
                    </>
                  )}
                  {tab === "sales" && (
                    <>
                      <p className="text-brand-text md:w-64 break-all">{r.customer_email || "—"}</p>
                      <p className="text-brand-dim flex-1">{r.lookup_key} · {r.kind || "ebook"}</p>
                      <span className="text-sm text-brand-text">${(r.amount || 0).toFixed(2)}</span>
                    </>
                  )}
                  {tab === "waitlist" && (
                    <>
                      <div className="md:w-56">
                        <p className="text-brand-text">{r.name}</p>
                        <p className="text-sm text-brand-gold">{r.email}</p>
                      </div>
                      <p className="text-brand-dim flex-1">{r.session_type} · {r.preferred_date || "any date"}{r.note ? ` · ${r.note}` : ""}</p>
                      <span className="text-xs text-brand-dim/60">{(r.created_at || "").slice(0, 10)}</span>
                      <button
                        data-testid={`waitlist-delete-${r.id}`}
                        onClick={() => deleteWaitlistEntry(r.id)}
                        className="ml-2 text-brand-dim hover:text-red-400 transition-colors"
                        title="Remove entry"
                      >
                        <Trash size={18} />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        )}
      </div>

      {previewPost && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" data-testid="blog-preview-modal" onClick={() => setPreviewPost(null)}>
          <div className="max-w-2xl w-full max-h-[85vh] overflow-y-auto rounded-3xl border border-brand-line bg-brand-surface p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <span className="overline">Preview · {previewPost.status === "scheduled" ? "Scheduled" : previewPost.published ? "Published" : "Draft"}</span>
              <button data-testid="preview-close" onClick={() => setPreviewPost(null)} className="text-brand-dim hover:text-brand-text">
                <X size={20} />
              </button>
            </div>
            <h2 className="font-serif text-3xl text-brand-text">{previewPost.title}</h2>
            {previewPost.excerpt && <p className="text-brand-dim mt-2">{previewPost.excerpt}</p>}
            {previewPost.image && <img src={previewPost.image} alt="" className="w-full rounded-2xl border border-brand-line mt-5 max-h-72 object-cover" />}
            <div className="mt-5 text-brand-dim whitespace-pre-wrap leading-relaxed">{previewPost.body}</div>
          </div>
        </div>
      )}
    </main>
  );
}
